import { Button } from '@/common/ui/button'
import { parseAsString, useQueryState } from 'nuqs'
import { useDateFilter } from '@/common/hooks/useDateFilter'
import { useState, useEffect } from 'react'
import {
	startOfDay,
	endOfDay,
	startOfMonth,
	endOfMonth,
	subDays,
	subMonths,
	format,
} from 'date-fns'

const dateRangeOptions = [
	{
		label: 'ทั้งหมด',
		value: 'all',
	},
	{
		label: '7 วันที่แล้ว',
		value: '7days',
	},
	{
		label: 'เดือนนี้',
		value: 'thisMonth',
	},
	{
		label: 'เดือนที่แล้ว',
		value: 'lastMonth',
	},
]

export default function RetentionDateRangeButtons() {
	const [dateRange, setDateRange] = useQueryState(
		'dateRange',
		parseAsString.withDefault('7days'),
	)
	const { dateFilter, setDateFilter } = useDateFilter()
	// Local stack state for this component only
	const [lastMonthStack, setLastMonthStack] = useState(0)
	const [sevenDaysStack, setSevenDaysStack] = useState(0)

	const resetStacks = () => {
		setLastMonthStack(0)
		setSevenDaysStack(0)
	}

	// Sync preset + stack when date range changes (e.g. from calendar or matches a preset pattern)
	useEffect(() => {
		if (!dateFilter.startDate || !dateFilter.endDate) {
			return
		}

		const today = startOfDay(new Date())
		const start = new Date(dateFilter.startDate)
		const end = new Date(dateFilter.endDate)
		const startStr = format(start, 'yyyy-MM-dd')
		const endStr = format(end, 'yyyy-MM-dd')

		// Check thisMonth (current month only)
		const thisMonthStart = format(startOfMonth(today), 'yyyy-MM-dd')
		const thisMonthEnd = format(endOfMonth(today), 'yyyy-MM-dd')
		if (startStr === thisMonthStart && endStr === thisMonthEnd) {
			setDateRange('thisMonth')
			resetStacks()
			return
		}

		// Check any 7-day window for "7days" + stack (1-based: 1=ล่าสุด, 2=1 สัปดาห์ก่อน)
		const diffDays = Math.round(
			(end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24),
		)
		if (diffDays === 6) {
			const daysFromTodayToEnd = Math.round(
				(today.getTime() - end.getTime()) / (1000 * 60 * 60 * 24),
			)
			if (daysFromTodayToEnd >= 0) {
				const stackOneBased = Math.floor(daysFromTodayToEnd / 7) + 1
				setDateRange('7days')
				setSevenDaysStack(stackOneBased)
				setLastMonthStack(0)
				return
			}
		}

		// Check any single month in the past for "lastMonth" + stack (1-based)
		const isStartOfMonth =
			startStr === format(startOfMonth(start), 'yyyy-MM-dd')
		const isEndOfMonth = endStr === format(endOfMonth(end), 'yyyy-MM-dd')
		if (isStartOfMonth && isEndOfMonth && end < today) {
			const monthsDiff =
				(today.getFullYear() - end.getFullYear()) * 12 +
				(today.getMonth() - end.getMonth())
			if (monthsDiff >= 1) {
				setDateRange('lastMonth')
				setLastMonthStack(monthsDiff)
				setSevenDaysStack(0)
				return
			}
		}

		// No preset matched (custom range)
		setDateRange('')
		resetStacks()
	}, [dateFilter.startDate, dateFilter.endDate, setDateRange])

	const resetOppositeStack = (type: '7days' | 'lastMonth') => {
		if (type === '7days') {
			setLastMonthStack(0)
		} else if (type === 'lastMonth') {
			setSevenDaysStack(0)
		}
	}

	const handleDateRangeChange = (value: string) => {
		setDateRange(value)
		const today = startOfDay(new Date())

		// Reset stacks when selecting non-stackable options
		if (value !== '7days' && value !== 'lastMonth') {
			resetStacks()
		}
		// Clear opposite stack when switching between stackable options
		else if (value === '7days') {
			setLastMonthStack(0)
		} else if (value === 'lastMonth') {
			setSevenDaysStack(0)
		}

		switch (value) {
			case 'all': {
				setDateFilter({
					startDate: '',
					endDate: '',
				})
				break
			}
			case '7days': {
				// Next period on each click (1-based: 1=ล่าสุด, 2=1 สัปดาห์ก่อน)
				const nextStack = sevenDaysStack + 1
				setSevenDaysStack(nextStack)

				const endDate = subDays(today, (nextStack - 1) * 7)
				const startDate = subDays(endDate, 6)

				setDateFilter({
					startDate: format(startOfDay(startDate), 'yyyy-MM-dd'),
					endDate: format(endOfDay(endDate), 'yyyy-MM-dd'),
				})
				break
			}
			case 'thisMonth': {
				setDateFilter({
					startDate: format(startOfMonth(today), 'yyyy-MM-dd'),
					endDate: format(endOfMonth(today), 'yyyy-MM-dd'),
				})
				break
			}
			case 'lastMonth': {
				const nextStack = lastMonthStack + 1
				setLastMonthStack(nextStack)

				const targetMonth = subMonths(today, nextStack)
				setDateFilter({
					startDate: format(startOfMonth(targetMonth), 'yyyy-MM-dd'),
					endDate: format(endOfMonth(targetMonth), 'yyyy-MM-dd'),
				})
				break
			}
		}
	}

	// Helper: stack is 1-based (1=ล่าสุด, 2=1 สัปดาห์/เดือนก่อน)
	const getDisplayLabel = (option: { label: string; value: string }) => {
		if (option.value === '7days') {
			if (sevenDaysStack <= 0) return option.label
			if (sevenDaysStack === 1) return '7 วันที่แล้ว (ล่าสุด)'
			return `7 วันที่แล้ว (${sevenDaysStack} สัปดาห์ก่อน)`
		}
		if (option.value === 'lastMonth') {
			if (lastMonthStack <= 0) return option.label
			return `เดือนที่แล้ว (${lastMonthStack} เดือนก่อน)`
		}
		return option.label
	}

	return (
		<div className="flex flex-wrap gap-2">
			{dateRangeOptions.map((option) => (
				<Button
					key={option.value}
					variant={
						dateRange === option.value && dateRange !== ''
							? 'default'
							: 'outline'
					}
					size="sm"
					onClick={() => handleDateRangeChange(option.value)}
				>
					{getDisplayLabel(option)}
				</Button>
			))}
		</div>
	)
}
